import { AnyExtension, Extension } from '@tiptap/core';

import Bold from './marks/Bold';
import Italic from './marks/Italic';

import BulletList from './nodes/BulletList';
import ListItem from './nodes/ListItem';
import Table from './nodes/Table';
import TableCell from './nodes/TableCell';
import TableHeader from './nodes/TableHeader';
import TableRow from '@tiptap/extension-table-row';
import { Markdown } from 'tiptap-markdown';
import Paragraph from './nodes/Paragraph';
import Text from './nodes/Text';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { BoldToken, ItalicToken, BulletListToken, MathToken } from '@scale/shared/types/config';
import Math from '../math';
import FontFamily from '@tiptap/extension-font-family';
import Typography from '@tiptap/extension-typography';
import IFrame from './nodes/iframe';
import ResizableImage from './nodes/ResizableImage';
import { TextStyleExtended } from './marks/TextStyleExtended';
import { YoutubeExtended } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/plugins/markdown/nodes/YoutubeExtended';
import { TextAlignExtended } from './marks/TextAlignExtended';

interface MarkdownExtensionOptions {
  enableMath: boolean;
  enableHtml: boolean;
  enableSoftBreaks: boolean;
  enableSingleDollarTextMath: boolean;
  boldToken: BoldToken;
  italicToken: ItalicToken;
  bulletListToken: BulletListToken;
  mathToken: MathToken;
  enableCourseTools: boolean;
  isCoursePreview: boolean;
}

interface MarkdownExtensionStorage {
  boldToken: BoldToken;
  italicToken: ItalicToken;
  bulletListToken: BulletListToken;
  mathToken: MathToken;
}

const MarkdownExtension = Extension.create<MarkdownExtensionOptions, MarkdownExtensionStorage>({
  name: 'markdownExtensions',
  addOptions() {
    return {
      enableMath: false,
      enableCourseTools: false,
      enableHtml: false,
      enableSoftBreaks: false,
      enableSingleDollarTextMath: true,
      boldToken: BoldToken.ASTERISK,
      italicToken: ItalicToken.ASTERISK,
      bulletListToken: BulletListToken.ASTERISK,
      mathToken: MathToken.DOLLARS,
      isCoursePreview: false,
    };
  },
  addStorage() {
    return {
      boldToken: this.options.boldToken,
      italicToken: this.options.italicToken,
      bulletListToken: this.options.bulletListToken,
      mathToken: this.options.mathToken,
    };
  },
  addExtensions() {
    const { enableHtml, enableSoftBreaks, enableMath, enableCourseTools, isCoursePreview } =
      this.options;

    const extensions: AnyExtension[] = [
      Markdown.configure({
        breaks: enableSoftBreaks,
        html: enableHtml,
      }),
      Bold,
      Italic,
      BulletList.configure({ keepAttributes: true }),
      ListItem,
      Table,
      TableCell,
      TableHeader,
      TableRow,
      Paragraph,
      Text,
      Link,
    ];

    if (enableCourseTools) {
      extensions.push(
        Underline,
        Color,
        TextStyleExtended,
        Highlight.configure({ multicolor: true }),
        YoutubeExtended,
        FontFamily,
        Typography,
        IFrame,
        ResizableImage,
        TextAlignExtended,
      );
    } else if (!isCoursePreview) {
      extensions.push(Image.configure({ allowBase64: true }));
    }

    if (enableMath) {
      extensions.push(
        Math.configure({
          allowedRules: [MathToken.DOLLARS, MathToken.BRACKETS, MathToken.ESCAPED_BRACKETS],
          enableSingleDollarTextMath: this.options.enableSingleDollarTextMath ?? true,
        }),
      );
    }

    return extensions;
  },
});

export default MarkdownExtension;
